import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { StartPurchaseOrForefitProperty } from "../../redux/action/sellerAction";
import { formatPrice } from "../../helpers";
import SocketService from "../../Service/SocketService";
import useTranslationHook from "../hooks/useTranslationHook";
import { createPortal } from "react-dom";
import { guessDateTime } from "../../utils/dateUtils";
import DOMPurify from "dompurify";
import { handleCalculateFinalPrice } from "../../redux/slice/buyerSlice";
import {
    calFinalPrice,
    savePurchaseAgreement,
} from "../../redux/action/buyerAction";
import Shimmer from "./shimmer/Shimmer";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import Input from "./form/Input";
import { PurchaseModalValidationSchema } from "../../utils/validations";
import { errToast } from "../../utils";
const StartBuyingProcessModal = ({
    show,
    onClose,
    isPurchased,
    setIsPurchased,
    setIsForeFitted,
    bidPrice,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslationHook();

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(PurchaseModalValidationSchema),
    });

    const [isSubmiting, setIsSubmiting] = useState(false);
    const { account: isSeller } = useSelector((state) => state.profile);
    const { user_id } = useSelector((state) => state.auth.user) || {};
    let lang = useSelector((state) => state.translation.lang);
    const buyerPriceCalculator = useSelector(
        (state) => state.buyer.buyerPriceCalculator
    );
    // const { id, deposit_amount } = useSelector(
    //     (state) => state.seller.property.propertyData
    // );
    const propertyData = useSelector(
        (state) => state.seller.property.propertyData
    );
    const onSubmit = (data) => {
        if (!user_id || isSeller !== 0) navigate("/sign-in");
        setIsSubmiting(true);
        dispatch(
            savePurchaseAgreement({
                property: propertyData?.id,
                signature: data?.signature,
                term_condition: data?.agreement ? 1 : 0,
            })
        )
            .then((res) => {
                if (!res.error) {
                    dispatch(
                        StartPurchaseOrForefitProperty(propertyData?.id)
                    ).then(({ data }) => {
                        if (!data?.error) {
                            setIsPurchased(true);
                            setIsForeFitted(false);
                            SocketService.send("getNotifications", { user_id });
                        }
                    });
                }
            })
            .catch((err) => errToast(err))
            .finally(() => {
                setIsSubmiting(false);
            });
    };

    useEffect(() => {
        document.body.classList.toggle("modal-open", show);
        if (show) {
            calculateFinalPrice();
            reset();
        }
        return () => {
            document.body.classList.remove("modal-open");
        };
    }, [show]);

    const sanitizedDescription = () => ({
        __html: DOMPurify.sanitize(
            lang === "en"
                ? propertyData?.description
                : propertyData?.description_ar || ""
        ),
    });

    const calculateFinalPrice = () => {
        dispatch(calFinalPrice({ property_id: propertyData?.id }, t));
    };

    const serviceFeePercentage =
        Number(propertyData?.property_setting?.service_fee) || 0;
    const serviceFeeAmount = serviceFeePercentage
        ? (serviceFeePercentage / 100) *
          Number(buyerPriceCalculator.current_bid || 0)
        : 0;
    const totalWithServiceFee =
        buyerPriceCalculator.total_price == null
            ? null
            : Number(buyerPriceCalculator.total_price) + serviceFeeAmount;

    useEffect(() => {
        return () => {
            dispatch(handleCalculateFinalPrice({ reset: true }));
        };
    }, []);

    return createPortal(
        <>
            {!isPurchased ? (
                <div
                    className={`modal fade ${show ? "show" : ""} purchase-modal`}
                    tabIndex="-1"
                >
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header mb-0">
                                <h5 className="modal-title">
                                    {t("Purchase Agreement")}
                                </h5>

                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                    onClick={onClose}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <form
                                onSubmit={handleSubmit(onSubmit)}
                                autoComplete="off"
                                autoCapitalize="off"
                            >
                                <div className="modal-body">
                                    <div className="mb-4">
                                        <p className="mb0 sky-text">
                                            {
                                                t("Please review the purchase terms and sign to confirm your purchase.")
                                            }
                                        </p>
                                    </div>

                                    <div className="sky-bg mb-4">
                                        <h6>{t("Auction Details")}</h6>
                                        <table>
                                            <tr>
                                                <td width={"35%"}>
                                                    {t("Auction ID")}
                                                </td>
                                                <td width={"65%"}>
                                                    {`${propertyData?.auction_unique_id}`}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td width={"35%"}>
                                                    {t("Auction End Date")}
                                                </td>
                                                <td width={"65%"}>
                                                    {` ${guessDateTime(propertyData?.bidding_end, "DD-MM-YYYY", 0)} `}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{t("Item Description")}</td>
                                                <td className="item-description">
                                                    <div
                                                        dangerouslySetInnerHTML={sanitizedDescription()}
                                                    />
                                                </td>
                                            </tr>
                                        </table>
                                    </div>

                                    <div className="sky-bg mb-4">
                                        <h6>{t("Final price calculation")}</h6>
                                        <table>
                                            {buyerPriceCalculator.dataLoading ? (
                                                <Shimmer
                                                    type="rectangle"
                                                    width="100%"
                                                    height="20vh"
                                                    margin-top="0"
                                                    borderRadius="5%"
                                                ></Shimmer>
                                            ) : (
                                                <>
                                                    <tr>
                                                        <td>
                                                            {t("Bid Price")}
                                                        </td>
                                                        <td>
                                                            {buyerPriceCalculator.current_bid ===
                                                            null ? (
                                                                <span>
                                                                    {t("NA")}
                                                                </span>
                                                            ) : (
                                                                <span>
                                                                    {t(
                                                                        "Amount",
                                                                        {
                                                                            amount: formatPrice(
                                                                                buyerPriceCalculator.current_bid,
                                                                                lang
                                                                            ),
                                                                        }
                                                                    )}
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {serviceFeePercentage ? (
                                                        <tr>
                                                            <td>
                                                                {t(
                                                                    "Service Fee"
                                                                )}
                                                            </td>
                                                            <td>
                                                                <span>
                                                                    {t(
                                                                        "Amount",
                                                                        {
                                                                            amount: formatPrice(
                                                                                serviceFeeAmount,
                                                                                lang
                                                                            ),
                                                                        }
                                                                    )}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ) : null}
                                                    {/* Hiding the extra fees as per request
                                                    {Number(
                                                        buyerPriceCalculator.buyers_premium
                                                    ) ? (
                                                        <tr>
                                                            <td>
                                                                {t(
                                                                    "Buyer premium amount"
                                                                )}
                                                            </td>
                                                            <td>
                                                                {" "}
                                                                {buyerPriceCalculator.buyers_premium ===
                                                                null ? (
                                                                    <span>
                                                                        {t(
                                                                            "NA"
                                                                        )}
                                                                    </span>
                                                                ) : (
                                                                    <span>
                                                                        <em className="dirham">
                                                                            D
                                                                        </em>{" "}
                                                                        {t(
                                                                            "Amount",
                                                                            {
                                                                                amount: formatNumberByLocale(
                                                                                    buyerPriceCalculator.buyers_premium,
                                                                                    lang
                                                                                ),
                                                                            }
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ) : null}
                                                    */}
                                                    {/* Hiding the extra fees as per request
                                                    {Number(
                                                        buyerPriceCalculator.platform_fees
                                                    ) ? (
                                                        <tr>
                                                            <td>
                                                                {t(
                                                                    "Platform fees"
                                                                )}
                                                            </td>
                                                            <td>
                                                                {buyerPriceCalculator.platform_fees ===
                                                                null ? (
                                                                    <span>
                                                                        {t(
                                                                            "NA"
                                                                        )}
                                                                    </span>
                                                                ) : (
                                                                    <span>
                                                                        <em className="dirham">
                                                                            D
                                                                        </em>{" "}
                                                                        {t(
                                                                            "Amount",
                                                                            {
                                                                                amount: formatNumberByLocale(
                                                                                    buyerPriceCalculator.platform_fees,
                                                                                    lang
                                                                                ),
                                                                            }
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ) : null}
                                                    */}
                                                    {/* Hiding the extra fees as per request
                                                    {Number(
                                                        buyerPriceCalculator.taxes
                                                    ) ? (
                                                        <tr>
                                                            <td>{t("VAT")}</td>
                                                            <td>
                                                                {buyerPriceCalculator.taxes ===
                                                                null ? (
                                                                    <span>
                                                                        {t(
                                                                            "NA"
                                                                        )}
                                                                    </span>
                                                                ) : (
                                                                    <span>
                                                                        <em className="dirham">
                                                                            D
                                                                        </em>{" "}
                                                                        {t(
                                                                            "Amount",
                                                                            {
                                                                                amount: formatNumberByLocale(
                                                                                    buyerPriceCalculator.taxes,
                                                                                    lang
                                                                                ),
                                                                            }
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ) : null}
                                                    */}
                                                    <tr>
                                                        <td className="black-text">
                                                            <strong>
                                                                {t("Total")}
                                                            </strong>
                                                        </td>
                                                        <td>
                                                            <strong>
                                                                {" "}
                                                                {totalWithServiceFee ===
                                                                null ? (
                                                                    <span>
                                                                        {t(
                                                                            "NA"
                                                                        )}
                                                                    </span>
                                                                ) : (
                                                                    <span>
                                                                        {t(
                                                                            "Amount",
                                                                            {
                                                                                amount: formatPrice(
                                                                                    totalWithServiceFee,
                                                                                    lang
                                                                                ),
                                                                            }
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </strong>
                                                        </td>
                                                    </tr>
                                                </>
                                            )}
                                        </table>
                                    </div>

                                    <div className="sky-bg mb-4">
                                        <h6>{t("Terms And Conditions")}</h6>
                                        <div className="scroll">
                                            <p>
                                                {t(
                                                    "By signing this Purchase Agreement, you acknowledge and agree to:"
                                                )}
                                            </p>
                                            <ul>
                                                <li>
                                                    {t(
                                                        "Payment of the total amount shown above within 5 business days"
                                                    )}
                                                </li>
                                                <li>
                                                    {t(
                                                        "Collection or shipping arrangements to be made within 14 days"
                                                    )}
                                                </li>
                                                <li>
                                                    {t(
                                                        "All sales are final and non-refundable except as required by law"
                                                    )}
                                                </li>
                                                <li>
                                                    {t("itemSoldAsIsNotice")}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <Input
                                            type="text"
                                            id="signature"
                                            className="form-control"
                                            placeholder={t(
                                                "Type your full name"
                                            )}
                                            label={
                                                t("Digital Signature") + t(" *")
                                            }
                                            labelClassname="pb5"
                                            register={register}
                                            errors={errors}
                                            {...register("signature")}
                                        />
                                    </div>
                                    <p className="sky-text">
                                        {t(
                                            "By typing your name, you agree that this constitutes a legal signature."
                                        )}
                                    </p>
                                    <div className="mb-3">
                                        <input
                                            id="agreement"
                                            type="checkbox"
                                            className="css-checkbox"
                                            {...register("agreement")}
                                        />
                                        <label
                                            htmlFor="agreement"
                                            className="css-label"
                                        >
                                            {t(
                                                "I have read and agree to the terms and conditions of this Purchase Agreement"
                                            )}
                                        </label>
                                        {errors?.["agreement"]?.message ? (
                                            <span className="text-danger">
                                                {t(errors["agreement"].message)}
                                            </span>
                                        ) : (
                                            <></>
                                        )}
                                    </div>
                                </div>
                                <div className="d-flex">
                                    <button
                                        className="btn btn-sky btn-md width50"
                                        type={"button"}
                                        onClick={onClose}
                                    >
                                        {t("Cancel")}
                                    </button>
                                    <button
                                        className="btn btn-primary btn-md width50"
                                        type={"submit"}
                                        disabled={isSubmiting}
                                    >
                                        {isSubmiting
                                            ? t("Submiting")
                                            : t("Sign & Submit")}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            ) : (
                <div
                    className={`modal fade ${show ? "show" : ""} purchase-modal`}
                    tabIndex="-1"
                >
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-body">
                                <div className="text-center">
                                    <p>
                                        <img src="/img/check-L.svg" alt="" />
                                    </p>
                                    <h4>{t("Purchase Confirmed!")}</h4>
                                    <div className="mb-4">
                                        <p className="sky-text">
                                            {t(
                                                "Your purchase agreement has been successfully submitted and processed."
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <div className="sky-bg mb-4">
                                    <h6>{t("Confirmation Email Sent")}</h6>
                                    <p>
                                        {t(
                                            "A receipt and payment instructions have been sent to your registered email address."
                                        )}
                                    </p>
                                </div>

                                <div className="sky-bg">
                                    <h6>{t("Next Steps:")}</h6>
                                    <div className="scroll">
                                        <ul>
                                            <li>
                                                {t(
                                                    "Complete payment within 5 business days"
                                                )}
                                            </li>
                                            <li>
                                                {t(
                                                    "Arrange collection or shipping within 14 days"
                                                )}
                                            </li>
                                            <li>
                                                {t(
                                                    "Check your email for detailed instructions"
                                                )}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex">
                                <button
                                    className="btn btn-white btn-md btn-full"
                                    type="button"
                                    data-bs-dismiss="modal"
                                    onClick={onClose}
                                >
                                    {t("Close")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>,
        document.getElementById("root")
    );
};

export default StartBuyingProcessModal;
